import { VatRepercutedModelType } from '@teinor/erp/types/company/accounting/vatRepercuted'
import { connect, ConnectedProps } from 'react-redux'
import { useMemo } from 'react'
import { ComponentTabObject } from '../../../../../../../../../utils/hooks/useTabLogic'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { InputTableLeftTabsProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { WorkingCenterPayload } from '../../../../../../../../../utils/reducers/company/workingCenters'
import { useInvoiceType, useRegimeType } from '../../../../../../../../../utils/hooks/useTypeIdNumber'
import { FormElement, FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import ITHeaderText from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import { returnClassOfSpanTemplate } from '../../../../../../../../../utils/globals/tailwind'
import { displayDate } from '../../../../../../../../../utils/globals/date'
import { CustomerPayload, CustomersResult } from '../../../../../../../../../utils/reducers/customers/customer'
import { parseAddressToOneLine } from '../../../../../../../../../utils/globals/address'
import AppStyleDecider from '../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { invoiceTypeInversed } from '@teinor/erp/types/company/order/sell/sellTicket'

type HeaderVatRepercutedFinishedProps = InputTableLeftColumnCompleteRequiredProps & ReduxHeaderVatRepercutedFinished & {}

const HeaderVatRepercutedFinished: React.FC<HeaderVatRepercutedFinishedProps> = ({...rest }) => {
    return <AppStyleDecider DefaultComponent={HeaderVatRepercutedFinishedDefault} NewComponent={HeaderVatRepercutedFinishedNew} {...rest} />
}

const HeaderVatRepercutedFinishedDefault: React.FC<HeaderVatRepercutedFinishedProps> = ({ workingCenters, customers, updateDataURL, ...rest }) => {
    const { t } = useTwinTranslation()

    const tabs: ComponentTabObject = {
        'main': {
            component: HeaderVatRepercutedFinishedMain,
            text: t('dataVatRepercuted', 'Datos del IVA Repercutido'),
            extraProps: { workingCenters, customers }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} updateDataURL={updateDataURL} {...rest}/>
    )
}

const HeaderVatRepercutedFinishedNew: React.FC<HeaderVatRepercutedFinishedProps> = ({ workingCenters, customers, headerData, getCompleteData, setCompleteData, updateDataURL }) => {
    const { t } = useTwinTranslation()
    const { invoiceTypes } = useInvoiceType()
    const { regimeTypes } = useRegimeType()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(headerData?.WorkingCenterAddress), [headerData?.WorkingCenterAddress])
    const extraCustomerFields: FormElement<keyof VatRepercutedModelType>[] = []
    if (headerData?.CustomerId) {
        extraCustomerFields.push({
            name: 'CustomerId',
            component: 'Custom',
            render: ({ value }) => <ITHeaderText label={t('customer', 'Cliente')} value={(customers?.all?.[value]?.fiscal_name || customers?.all?.[value]?.name) ?? ''} />
        },
        {
            name: 'customer_date',
            component: 'Custom',
            render: ({ value }) => <ITHeaderText label={t('dateCustomer', 'Fecha del cliente')} value={value ? displayDate(new Date(value)) : ''} />
        },
        {
            name: 'customer_assigned_number',
            component: 'Custom',
            render: ({ value }) => <ITHeaderText label={t('numCustomer', 'Num. cliente')} value={value} />,
            hideSectionByDefault: true
        })
    }
    const sections: FormRender<VatRepercutedModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                ...extraCustomerFields,
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <SellNumber headerData = { headerData } value={value} />,
                },
                {
                    name: 'type',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('invoiceType', 'Tipo de factura')} value={invoiceTypes[value]?.name} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'regime',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('regime', 'Régimen')} value={regimeTypes[value]?.name} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('vatRepercutedDate', 'Fecha IVA Sop.')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedBy', 'Facturado por')} value={workingCenters?.[value]?.name || ''} />
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={extraProps.workingCenterAddress} />
                    },
                    extraProps: {
                        workingCenterAddress: workingCenterAddress
                    },
                    hideSectionByDefault: true
                },
            ]
        }
    ]
    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

interface HeaderVatRepercutedFinishedMainProps extends InputTableLeftTabsProps {
    workingCenters: WorkingCenterPayload
    customers: CustomersResult
}

const HeaderVatRepercutedFinishedMain: React.FC<HeaderVatRepercutedFinishedMainProps> = ({ workingCenters, customers, handleChange, headerData }) => {
    const { t } = useTwinTranslation()
    const { invoiceTypes } = useInvoiceType()
    const { regimeTypes } = useRegimeType()
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(headerData?.WorkingCenterAddress), [headerData?.WorkingCenterAddress])
    const extraCustomerFields: FormElement<keyof VatRepercutedModelType>[] = []
    if (headerData?.CustomerId) {
        extraCustomerFields.push({
            name: 'CustomerId',
            component: 'Custom',
            render: ({ value }) => <ITHeaderText label={t('customer', 'Cliente')} value={(customers?.all?.[value]?.fiscal_name || customers?.all?.[value]?.name) ?? ''} />
        },
            {
                name: 'customer_date',
                component: 'Custom',
                render: ({ value }) => <ITHeaderText label={t('dateCustomer', 'Fecha del cliente')} value={value ? displayDate(new Date(value)) : ''} />
            },
            {
                name: 'customer_assigned_number',
                component: 'Custom',
                render: ({ value }) => <ITHeaderText label={t('numCustomer', 'Num. cliente')} value={value} />,
                hideSectionByDefault: true
            })
    }
    const sections: FormRender<VatRepercutedModelType> = [
        {
            cols: 2,
            elements: [
                ...extraCustomerFields,
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <SellNumber headerData={headerData} value={value}/>,
                },
                {
                    name: 'type',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('invoiceType', 'Tipo de factura')} value={invoiceTypes[value]?.name} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'regime',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('regime', 'Régimen')} value={regimeTypes[value]?.name} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('vatRepercutedDate', 'Fecha IVA Sop.')} value={displayDate(new Date(value))} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedBy', 'Facturado por')} value={workingCenters?.[value]?.name || ''} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={extraProps.workingCenterAddress} className={returnClassOfSpanTemplate(2)} />
                    },
                    extraProps: {
                        workingCenterAddress: workingCenterAddress
                    }
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
    )
}

interface SellNumberProps {
    headerData?: TwinDictionary
    value: string
}

const SellNumber: React.FC<SellNumberProps> = ({ headerData, value }) => {
    const { t } = useTwinTranslation()
    const dictSellNumber: TwinDictionary = {
        'invoice': { label: t('invoiceShort', 'Fact.') },
        'invoiceRefund': { label: t('invoiceRefundShort', 'Dev. Fact.') },
        'ticketRefund': { label: t('ticketRefundShort', 'Dev. T.') },
        'ticketRefundInvoice': { label: t('ticketInvoiceRefundShort', 'Dev. T. F.') },
        'ticket': { label: t('ticket', 'Ticket') },
        'ticketInvoice': { label: t('invoiceTicket', 'Ticket Fact.') },
    }
    let typeSell = ''
    if (headerData?.OrderId) {
        typeSell = 'invoice'
    } else if (headerData?.SellInvoiceRefundId) {
        typeSell = 'invoiceRefund'
    } else if (headerData?.SellTicketRefundId) {
        typeSell = parseInt(headerData?.SellTicketRefund?.invoiceType) === invoiceTypeInversed['ticket'] ? 'ticketRefund' : 'ticketRefundInvoice'
    } else if (headerData?.SellTicketId) {
        typeSell = parseInt(headerData?.SellTicketRefund?.invoiceType) === invoiceTypeInversed['ticket'] ? 'ticket' : 'ticketInvoice'
    }

    return (<ITHeaderText label={t('vatRepercutedSingular', 'IVA Repercutido')} value={dictSellNumber[typeSell].label + ' ' + value} className={returnClassOfSpanTemplate(1)} />)
}



const headerVatRepercutedFinishedDispatch = {
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
    setCustomers: (payload: CustomerPayload) => ({ type: 'CHANGE_CUSTOMER', payload }),
}

export type ReduxHeaderVatRepercutedFinished = ConnectedProps<typeof headerVatRepercutedFinishedConnect>
const mapHeaderVatRepercutedFinishedConnector = (state: AllReduxPayloads) => ({ workingCenters: state.workingCenters, customers: state.customers, warehouses: state.warehouses, })
const headerVatRepercutedFinishedConnect = connect(mapHeaderVatRepercutedFinishedConnector, headerVatRepercutedFinishedDispatch)
const headerVatRepercutedFinishedConnectLoading = withLoading(HeaderVatRepercutedFinished, [{ fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }, { fetchUrl: '/api/app/customer/getAllCustomersComplete', propName: 'customers', setFunctionName: 'setCustomers' }])
const HeaderVatRepercutedFinishedConnect = headerVatRepercutedFinishedConnect(headerVatRepercutedFinishedConnectLoading)

export default HeaderVatRepercutedFinishedConnect